import { Component, Inject, OnDestroy, PLATFORM_ID } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ApiLiveStream, ApiPodcast, ApiProduction, ApiShow, ApiUser, ApiVOD, ShowsApi } from "@tytapp/api";
import { ProductionsService } from "@tytapp/common-ui";
import { CanonicalURLService } from "@tytapp/common/canonical-url.service";
import { AppConfig, formatDurationPT, Head, OpenGraph, Page, PageComponent, PageService, Redirection, sleep, SubscriptionSet, Transfer, zeroPad } from "common";
import { LiveStreamsService } from "common-ui/live-streams.service";
import { PlaylistType } from "media-playback";
import { UserService } from "user";

@Page('watch')
@Component({
    selector: 'tyt-watch-page',
    templateUrl: './watch-page.component.html',
    styleUrls: ['./watch-page.component.scss']
})
export class WatchPageComponent extends PageComponent implements OnDestroy {

    constructor(
        pageService: PageService,
        private route: ActivatedRoute,
        private productionsService: ProductionsService,
        private liveStreamsService: LiveStreamsService,
        private showsApi: ShowsApi,
        private userService: UserService,
        private appConfig: AppConfig,
        private redirection: Redirection,
        private opengraph: OpenGraph,
        private head: Head,

        @Inject(PLATFORM_ID)
        private platformId: any,

        private canonicalService: CanonicalURLService

    ) {
        super(pageService);
    }

    get pageComponentId() {
        return 'watch';
    }

    get title() {
        if (!this.currentItem)
            return null;

        return this.currentItem.description;
    }

    @Transfer()
    production: ApiProduction;

    @Transfer()
    show: ApiShow;

    @Transfer()
    activeLiveStreams: ApiLiveStream[];

    @Transfer()
    activeLiveStream: ApiLiveStream;

    @Transfer()
    mode: PlaylistType;

    @Transfer()
    currentItem: ApiVOD;

    @Transfer()
    user: ApiUser;

    @Transfer()
    playlist: any[];

    @Transfer()
    recentEpisodes: ApiProduction[] = [];

    @Transfer()
    showPlaybackOptions: boolean = false;

    @Transfer()
    startingVodID: any;

    subscriptions: SubscriptionSet = new SubscriptionSet();

    audienceType: string = 'visitor';

    get loginOrJoinUrl() {
        if (!this.user)
            return `/login`;
        return `/join`;
    }

    get entitled() {
        if (!this.user)
            return false;

        if (this.user.entitled)
            return true;

        if (this.user.membership && this.user.membership.entitled)
            return true;

        return false;
    }

    destroy() {
        this.subscriptions.unsubscribeAll();
    }

    loading = true;
    notFound = true;

    async init() {
        this.subscriptions.subscribe(this.userService.userChanged, user => this.user = user);

        this.route.paramMap.subscribe(async params => {
            this.loading = true;
            await sleep(1);

            let showID = params.get('show');
            let productionID = params.get('production');
            let mode: PlaylistType = <PlaylistType>params.get('mode');
            let vodID = params.get('vod');

            let verb = 'watch';

            if (mode === 'podcasts')
                verb = 'listen';

            this.notFound = false;
            this.startingVodID = vodID;

            if (!this.hasServerState) {
                let production: ApiProduction;

                try {
                    production = await this.productionsService.get(productionID);
                } catch (e) {
                    if (e.statusCode === 404) {
                        production = null;
                    } else {
                        this.redirection.go('/');
                        throw e;
                    }
                }

                if (!production) {
                    this.redirection.go('/');
                    return;
                }

                this.mode = mode;
                this.production = production;
                this.show = this.production.show as ApiShow;
                this.activeLiveStreams = await this.liveStreamsService.active() as ApiLiveStream[];
                this.activeLiveStream = this.activeLiveStreams.find(x => this.show && x.active_live_event && x.active_live_event.show && x.active_live_event.show.id == this.production.show.id);
                this.show = await this.showsApi.get(this.production.show.id).toPromise() as ApiShow;

                if (showID)
                    this.head.setCanonicalLink(`/${verb}/${this.show.slug || this.show.id}/${productionID}/${mode}/${vodID}`);
                else if (vodID)
                    this.head.setCanonicalLink(`/${verb}/${productionID}/${mode}/${vodID}`);
                else
                    this.head.setCanonicalLink(`/${verb}/${productionID}/${mode}`);

                if (this.production && this.production.show) {
                    this.pageTitle = `${this.production.show.name} ${this.production.date_simple}`;
                } else {
                    this.pageTitle = 'Watch';
                }

                await sleep(1);
                let playlist: (ApiVOD | ApiPodcast)[] = this.production.full_length_vods;

                if (this.mode == 'clips')
                    playlist = this.production.vod_clips;
                else if (this.mode == 'podcasts')
                    playlist = this.production.full_length_podcasts;

                if (!playlist) {
                    console.error(`Failed to select playlist for mode '${this.mode}'`);
                    playlist = [];
                }

                this.playlist = playlist;


                if (this.mode == 'episodes')
                    this.recentEpisodes = await this.showsApi.getShowEpisodes(this.show.id, 0, 2).toPromise();
                else if (this.mode == 'clips')
                    this.recentEpisodes = await this.showsApi.getShowClips(this.show.id, 0, 2).toPromise();
                else if (this.mode == 'podcasts')
                    this.recentEpisodes = await this.showsApi.getShowPodcasts(this.show.id, 0, 2).toPromise();

                this.recentEpisodes = this.recentEpisodes.filter(x => x.id != this.production.id);


                let vod = this.production.vod_clips.find(x => x.id === vodID);
                if (!vod)
                    vod = this.production.full_length_vods.find(x => x.id === vodID);

                if (vod) {
                    let selfUrl = `https://tyt.com/watch/${this.production.show.id}/${this.production.id}/${mode}/${vod.id}`;

                    this.opengraph.setMetadata({
                        title: vod.title,
                        description: vod.description,
                        type: 'video.movie',
                        url: selfUrl,
                        siteName: 'TYT.com',
                        image: vod.thumbnail_landscape1
                    });

                    let exposeVideoContentInLD = await this.appConfig.featureEnabled('apps.web.ld_expose_video');

                    if (exposeVideoContentInLD) {
                        try {
                            this.opengraph.setLdJson({
                                "@context": "http://schema.org",
                                "@type": "VideoObject",
                                url: selfUrl,
                                name: vod.title || vod.description,
                                description: vod.description,
                                width: 1920,
                                height: 1080,
                                playerType: "HLS",
                                videoQuality: "HD",
                                thumbnailUrl: vod.thumbnail_landscape1 || production.thumbnail_landscape1 || this.show.thumbnail_landscape1,
                                contentUrl: vod.asset && exposeVideoContentInLD ? vod.asset.url : undefined,
                                duration: formatDurationPT(parseInt(<any>vod.duration)),
                                uploadDate: new Date(this.production.date).toISOString()
                            });
                        } catch (e) {
                            console.error(`Failed to set LD+JSON for video:`);
                            console.error(e);
                        }
                    }
                }
            }

            let vod = this.playlist[0];
            if (this.startingVodID)
                this.currentItem = vod = this.playlist.find(x => x.id == this.startingVodID);

            const routePrefix = mode === 'episodes' || mode === 'clips' ? 'watch' :
                mode === 'podcasts' ? 'listen' : 'watch';

            // Setting Canonical URL
            this.canonicalService.setCanonicalURLManually(`/${routePrefix}/${this.show.slug || this.show.id}/${this.production.id}/${mode}${vod ? `/${vod.id}` : ''}`);

            this.loading = false;
        });

        this.audienceType = this.userService.audienceType;
    }
}
